import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { BUILT, createElt } from './elt-model';
import { BACKGROUND, SKY_RADIUS, createSky } from './sky';

// One still frame of the Santiago scene — the built ELT under the real sky of 20 Nov 2026
// — from wherever the camera is put: for the share card (Landings/Og), which is
// screenshotted as it is. Nothing moves; it renders once.

export type EltShot = {
  camera: { pos: [number, number, number]; look: [number, number, number]; fov: number };
  // where the telescope points (az/alt in the scene's frame: az 0 away from the landing's
  // camera, 180 toward it), the dome turned with it
  aim: { az: number; alt: number };
  lasers?: number; // the four laser guide stars, 0–1
  bloom?: number;
};

export function EltStill({ shot, className }: { shot: EltShot; className?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const width = host.clientWidth;
    const height = host.clientHeight;
    const pixelRatio = 2; // crisp in the screenshot
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, preserveDrawingBuffer: true });
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height, false);
    renderer.setClearColor(new THREE.Color(BACKGROUND).convertSRGBToLinear(), 1);
    Object.assign(renderer.domElement.style, { display: 'block', width: '100%', height: '100%' });
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(shot.camera.fov, width / height, 0.5, SKY_RADIUS * 3);
    camera.position.set(...shot.camera.pos);
    camera.lookAt(new THREE.Vector3(...shot.camera.look));

    const composer = new EffectComposer(renderer, new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 4 }));
    composer.setPixelRatio(pixelRatio);
    composer.setSize(width, height);
    composer.addPass(new RenderPass(scene, camera));
    composer.addPass(new UnrealBloomPass(new THREE.Vector2(width, height), shot.bloom ?? 0.32, 0.4, 0.55));
    composer.addPass(new OutputPass());

    const sky = createSky(pixelRatio);
    sky.uniforms.uTime.value = 60; // every star lit
    scene.add(sky.group);

    const clock = { value: BUILT + 5 };
    const elt = createElt(clock);
    elt.setDoors(1);
    elt.setCranes(clock.value, 0);
    elt.setPointing(shot.aim.az, shot.aim.alt, shot.aim.az);
    elt.setLasers(shot.lasers ?? 0, 3);
    elt.setLightPath(shot.lasers ? 1 : 0.35, 3);
    scene.add(elt.root);

    composer.render();

    return () => {
      sky.dispose();
      elt.dispose();
      composer.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [shot]);

  return <div ref={hostRef} className={className} />;
}
